import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import Grid from "@mui/material/Grid2";
import { Button, Typography } from "@mui/material";
import { isEmpty } from "lodash";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  AddTrainingVideo,
  GeneratedUrl,
  TrainingVideo,
} from "../../../models/Training";
import { getCurrnetPageFullPath, getUaserPageViewPayload, getValue, hasAccess, length } from "../../../utils";
import VideoCard from "../containers/VideoCard";
import { MyAccountModel } from "../../../models/MyAccount";
import {
  CONFIRM_DELETE,
  defaultViewVideoTrainingData,
  FILE_DELETED,
  TRAINING_PORTAL_MESSAGES,
} from "../../../constants/Training";
import { ApplicationTitles, UserRole } from "../../../constants/HomePage";
import { BoxCenter, GridContainer, WelcomeBox } from "../styles/style";
import AddEditTraining from "../containers/AddEditTraining";
import { ApplicationRoute } from "../../../constants/ApplicationUrl";
import { Numbers } from "../../../constants/Referral";
import { FeatureAccess } from "../../../models/Master";
import { UserContext, UserContextType } from "../../../app/components/AppContent";
import { redirectUri } from "../../../constants/Config";
import { getValidMessageType } from "../../../constants/User";
import { UserPageTrack } from "../../../models/User";

export interface PropsFromState {
  trainingVideoList: TrainingVideo[];
  loggedInUser: MyAccountModel;
  deleteTrainingVideoState: string;
  addTrainingVideoState: AddTrainingVideo;
  generatedVideoUrl: GeneratedUrl;
  editTrainingVideoState: AddTrainingVideo;
  masterFeatureAccessState: FeatureAccess[] | null;
}

export interface PropsFromDispatch {
  getTrainingVideo: () => void;
  deleteTrainingVideo: (videoId: number) => void;
  resetGetTrainingVideo: () => void;
  resetDeleteTrainingVideo: () => void;
  resetPostTrainingVideo: () => void;
  getGeneratedVideoUrl: (url: string) => void;
  resetGeneratedVideoUrl: () => void;
  resetPutTrainingVideo: () => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const Training: React.FC<AllProps> = ({
  trainingVideoList,
  getTrainingVideo,
  deleteTrainingVideo,
  loggedInUser,
  deleteTrainingVideoState,
  resetGetTrainingVideo,
  resetDeleteTrainingVideo,
  addTrainingVideoState,
  resetPostTrainingVideo,
  getGeneratedVideoUrl,
  resetGeneratedVideoUrl,
  generatedVideoUrl,
  editTrainingVideoState,
  resetPutTrainingVideo,
  masterFeatureAccessState,
  postUserPageTrack
}) => {
  const { roleId } = useContext<UserContextType>(UserContext);
  const [trainingVideoData, setTrainingVideoData] = useState<TrainingVideo[]>(
    []
  );
  const [openAddTrainingModal, setOpenAddTrainingModal] =
    useState<boolean>(false);
  const [viewVideoData, setViewVideoData] = useState<TrainingVideo>(
    defaultViewVideoTrainingData
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (!isEmpty(generatedVideoUrl.url)) {
      navigate(ApplicationRoute.VIDEO_PLAYER, {
        state: { videoUrl: generatedVideoUrl.url, viewVideoData },
      });
    }
  }, [generatedVideoUrl, viewVideoData]);

  useEffect(() => {
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.TRAINING),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
    return () => {
      resetGeneratedVideoUrl();
    };
  }, []);

  useEffect(() => {
    getTrainingVideo();
  }, []);

  useEffect(() => {
    if (
      getValue(addTrainingVideoState, "videoId", Numbers.ZERO) > Numbers.ZERO
    ) {
      getTrainingVideo();
      resetPostTrainingVideo();
    }
  }, [addTrainingVideoState]);

  useEffect(() => {
    if (
      getValue(editTrainingVideoState, "videoId", Numbers.ZERO) > Numbers.ZERO
    ) {
      getTrainingVideo();
      resetPutTrainingVideo();
    }
  }, [editTrainingVideoState]);

  useEffect(() => {
    if (deleteTrainingVideoState === FILE_DELETED) {
      getTrainingVideo();
      resetDeleteTrainingVideo();
    }
  }, [deleteTrainingVideoState]);

  useEffect(() => {
    setTrainingVideoData(trainingVideoList);
  }, [trainingVideoList]);

  const handleDeleteVideo = (videoId: number) => {
    const confirmDelete = window.confirm(CONFIRM_DELETE);

    if (confirmDelete) {
      deleteTrainingVideo(videoId);
      resetGetTrainingVideo();
    }
  };

  const handleViewVideo = (video: TrainingVideo) => {
    getGeneratedVideoUrl(video.url);
    setViewVideoData(video);
  };

  const renderWelcomeBox = (subTitle: string, showButton?: boolean) => {
    return (
      <Box sx={WelcomeBox}>
        <Box sx={BoxCenter}>
          <Typography
            variant="h1"
            color={colors.portalColor[2]}
            fontWeight={fontWeight[3]}
            fontSize={"2rem"}
            mb={"1.2rem"}
          >
            {TRAINING_PORTAL_MESSAGES.welcomeMessage}
          </Typography>
          <Typography
            variant="h2"
            color={colors.portalColor[2]}
            fontWeight={fontWeight[2]}
            mb={"1.2rem"}
          >
            {subTitle}
          </Typography>
          {showButton && (
            <Button
              variant="contained"
              onClick={() => setOpenAddTrainingModal(true)}
            >
              Get Started
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const featureToggelBaseMessageBox = (featureAccess: FeatureAccess[] | null) => {
    return (
      <Box sx={WelcomeBox}>
        <Box sx={BoxCenter}>
          <Typography
            variant="h1"
            color={colors.portalColor[2]}
            fontWeight={fontWeight[3]}
            fontSize={"2rem"}
            mb={"1.2rem"}
          >
            {getFeatureDescription(featureAccess)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderRoleBasedWelcomeBox = () => {
    const role = getValue(loggedInUser, "providerPortalUser.roleType");
    if (role === UserRole.TANGO_ADMIN) {
      return renderWelcomeBox(TRAINING_PORTAL_MESSAGES.tangoAdminMessage, true);
    }
    return renderWelcomeBox(TRAINING_PORTAL_MESSAGES.otherUserMessage);
  };

  const getFeatureDescription = (featureAccess: FeatureAccess[] | null) => {
    const description = featureAccess && featureAccess.find(
      (feature) => feature.featureId === Numbers.Four)?.description;
    return description ;
   }

  return (<Box sx={GridContainer}>
    {!hasAccess(roleId, masterFeatureAccessState, Numbers.Four) ? featureToggelBaseMessageBox(masterFeatureAccessState)  :
      <Box sx={GridContainer}>
        {trainingVideoData && length(trainingVideoData) ? (
          <Grid container pt={1}>
            <Grid size={{ xs: 12 }} pb={3}>
              <Typography
                variant="h1"
                color={colors.portalColor[2]}
                fontWeight={fontWeight[3]}
                fontSize={"2.5rem"}
                align="center"
              >
                Trainings
              </Typography>
              {getValue(loggedInUser, "providerPortalUser.roleType") ===
                UserRole.TANGO_ADMIN && (
                  <Button
                    variant="contained"
                    onClick={() => setOpenAddTrainingModal(true)}
                  >
                    Add Training
                  </Button>
                )}
            </Grid>
            {trainingVideoData.map((data: TrainingVideo) => {
              return (
                <Grid size={{ xs: 4.5 }} key={data.videoID} mr={4}>
                  <VideoCard
                    trainingVideoData={data}
                    handleDeleteVideo={handleDeleteVideo}
                    handleViewVideo={handleViewVideo}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          renderRoleBasedWelcomeBox()
        )}
        {openAddTrainingModal && (
          <AddEditTraining
            isOpenModal={openAddTrainingModal}
            setIsOpenModal={setOpenAddTrainingModal}
            loggedInUser={loggedInUser}
          />
        )}
      </Box>}
    </Box>
  );
};

export default Training;
